import React, { useEffect, useState, useCallback } from "react";
import { auth } from "@/config/firebase";
import { cn } from "@/lib/utils";
import { CoinDisplay, OddsBadge } from "@/components/economy/OddsBadge";
import { 
  TrendingUp, TrendingDown, Lock, Gift, AlertCircle, 
  CheckCircle, XCircle, ChevronDown, ChevronUp, Coins
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/**
 * PredictionMarket - Season Prediction Betting Interface
 * 
 * Features:
 * - Display predictions with fixed odds from Phase A
 * - Place bets on YES/NO
 * - Show author freebet for own predictions
 * - Side lock enforcement
 */
export function PredictionMarket({ seasonId = "2026-27", className, wallet }) {
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedPrediction, setExpandedPrediction] = useState(null);
  const [windowInfo, setWindowInfo] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());

  const fetchMarket = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        setError("Nicht angemeldet");
        setLoading(false);
        return;
      }

      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE}/api/predictions-market/predictions?season_id=${seasonId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) throw new Error(`API error: ${res.status}`);
      
      const data = await res.json();
      setMarketData(data);
      setLoading(false);
      setError(null);
      // Fetch timed window info (non-blocking)
      try {
        const wres = await fetch(`${API_BASE}/api/market-ops/window?season_id=${seasonId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (wres.ok) setWindowInfo(await wres.json());
      } catch (e) { /* ignore */ }
    } catch (err) {
      console.error("Market fetch error:", err);
      setError(err.message);
      setLoading(false);
    }
  }, [seasonId]);

  useEffect(() => {
    fetchMarket();
    const interval = setInterval(fetchMarket, 10000);
    return () => clearInterval(interval);
  }, [fetchMarket]);

  // 1s tick for the live countdown
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (loading) {
    return (
      <div className={cn("flex items-center justify-center py-12", className)}>
        <div className="animate-spin w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn("text-red-400 text-center py-8", className)}>
        <AlertCircle className="w-8 h-8 mx-auto mb-2" />
        <p>{error}</p>
      </div>
    );
  }

  if (!marketData) {
    return (
      <div className={cn("text-white/50 text-center py-8", className)}>
        Markt nicht verfügbar
      </div>
    );
  }

  const { marketStatus, predictions, marketPhase = 1, referenceAvgStake = 0 } = marketData;

  // Nur echte Predictions: 0%-Quoten (übersprungene Duplikate) ausblenden
  const visiblePreds = (predictions || []).filter(
    (p) => (p.teamProbability ?? 0) > 0
  );
  const balance = wallet?.balance ?? 0;
  const reserved = wallet?.reserved ?? 0;
  const locked = wallet?.lockedBalance ?? wallet?.locked ?? 0;
  const earned = wallet?.earnedBalance ?? wallet?.earned ?? 0;
  const available = Math.max(0, Math.round((balance - reserved) * 10) / 10);

  // "Deine Wetten"-Übersicht aus den aktuellen Positionen
  let myStake = 0;
  let myPayout = 0;
  let myCount = 0;
  visiblePreds.forEach((p) => {
    const pos = p.userPosition;
    if (!pos) return;
    const yes = pos.totalYesStake || 0;
    const no = pos.totalNoStake || 0;
    if (yes > 0 || no > 0) myCount += 1;
    myStake += yes + no;
    myPayout += yes * (p.yesOdds || 0) + no * (p.noOdds || 0);
  });

  // Timed window banner (Europe/Berlin display)
  const closesAt = windowInfo?.closesAt ? new Date(windowInfo.closesAt) : null;
  const isExpired = windowInfo?.isExpired || (closesAt && nowTick > closesAt.getTime());
  const accepting = marketStatus === 'OPEN' && !isExpired;
  let countdownText = '';
  if (accepting && closesAt) {
    const ms = Math.max(0, closesAt.getTime() - nowTick);
    const d = Math.floor(ms / 86400000);
    const h = Math.floor((ms % 86400000) / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    countdownText = d > 0 ? `Noch ${d} Tage ${h} Std` : (h > 0 ? `Noch ${h} Std ${m} Min` : `Noch ${m} Min`);
  }

  return (
    <div className={cn("space-y-4", className)}>
      {/* Wallet-Klarheit: wie viel kann ich noch verwetten? */}
      <div className="grid grid-cols-3 gap-2" data-testid="wallet-clarity">
        <div className="rounded-xl bg-green-500/10 border border-green-500/30 p-3 text-center">
          <div className="text-lg font-bold text-green-300" data-testid="wallet-available">{available}</div>
          <div className="text-[11px] text-white/60">frei zum Wetten</div>
        </div>
        <div className="rounded-xl bg-white/5 border border-white/10 p-3 text-center">
          <div className="text-lg font-bold text-yellow-300">{Math.round(reserved)}</div>
          <div className="text-[11px] text-white/50">in Wetten</div>
        </div>
        <div className="rounded-xl bg-white/5 border border-white/10 p-3 text-center">
          <div className="text-lg font-bold text-purple-300">{Math.round(locked)}</div>
          <div className="text-[11px] text-white/50">gesperrte Gewinne</div>
        </div>
      </div>

      {earned > 0 && (
        <div className="rounded-xl bg-indigo-500/10 border border-indigo-500/30 p-2.5 text-center flex items-center justify-center gap-2" data-testid="wallet-earned">
          <span className="text-base font-bold text-indigo-300">{Math.round(earned)}</span>
          <span className="text-[12px] text-indigo-100/80">verdiente Coins (Training/Spiel) — <b>ab Phase 2 wettbar</b>, zählen schon fürs Ranking</span>
        </div>
      )}

      {/* Phasen-Banner */}
      {marketPhase === 2 ? (
        <div className="rounded-xl p-3 border text-center bg-gradient-to-r from-purple-600/20 to-blue-600/10 border-purple-500/40" data-testid="phase-banner-2">
          <div className="text-purple-200 font-bold text-sm">⚡ Phase 2 — dynamische Quoten (live)</div>
          <div className="text-[12px] text-white/60 mt-1">
            Jetzt zählen nur noch <b>frisch verdiente Coins</b>. Viele Coins auf einer Wette → Quote sinkt. Wenig/keine → Quote steigt. Deine Wette lockt die aktuelle Quote.
          </div>
        </div>
      ) : (
        <div className="rounded-xl p-2.5 border text-center bg-white/5 border-white/10" data-testid="phase-banner-1">
          <div className="text-[12px] text-white/70">
            <b className="text-orange-300">Phase 1</b> — feste Quoten. Verwette dein Guthaben, bevor es verfällt! Danach startet Phase 2 mit dynamischen Quoten.
          </div>
        </div>
      )}

      {/* Timed Window Banner */}
      {accepting ? (
        <div className="rounded-xl p-4 border text-center bg-gradient-to-r from-orange-600/20 to-red-600/10 border-orange-500/40" data-testid="market-window-open">
          <div className="flex items-center justify-center gap-2 text-orange-300">
            <TrendingUp className="w-5 h-5" />
            <span className="font-bold">🔥 Wettfenster offen</span>
          </div>
          {countdownText && (
            <div className="mt-1 text-2xl font-black text-white tabular-nums" data-testid="market-countdown">{countdownText}</div>
          )}
          {closesAt && (
            <div className="text-xs text-orange-200/60 mt-1">
              schließt {closesAt.toLocaleString('de-DE', { timeZone: 'Europe/Berlin', dateStyle: 'medium', timeStyle: 'short' })} Uhr
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-xl p-4 border text-center bg-yellow-500/10 border-yellow-500/30 text-yellow-400" data-testid="market-window-closed">
          <div className="flex items-center justify-center gap-2">
            <Lock className="w-5 h-5" />
            <span className="font-medium">
              {isExpired ? '🔒 Wettfenster beendet — keine neuen Wetten. Bestehende Wetten bleiben im Portfolio sichtbar.' : 'Markt ist noch geschlossen'}
            </span>
          </div>
        </div>
      )}

      {/* Deine-Wetten-Übersicht */}
      {myCount > 0 && (
        <div className="grid grid-cols-3 gap-2" data-testid="my-bets-summary">
          <div className="rounded-xl bg-white/5 border border-white/10 p-3 text-center">
            <div className="text-lg font-bold text-white">{myCount}</div>
            <div className="text-[11px] text-white/50">Wetten offen</div>
          </div>
          <div className="rounded-xl bg-white/5 border border-white/10 p-3 text-center">
            <div className="text-lg font-bold text-yellow-300">{Math.round(myStake)}</div>
            <div className="text-[11px] text-white/50">Coins gesetzt</div>
          </div>
          <div className="rounded-xl bg-white/5 border border-white/10 p-3 text-center">
            <div className="text-lg font-bold text-green-300">{Math.round(myPayout)}</div>
            <div className="text-[11px] text-white/50">mögl. Auszahlung</div>
          </div>
        </div>
      )}

      {/* So funktioniert's — kurz */}
      {accepting && (
        <div className="rounded-xl bg-blue-500/5 border border-blue-500/20 p-4 text-sm space-y-1.5" data-testid="market-rules">
          <div className="flex items-center gap-2 text-blue-300 font-semibold">
            <AlertCircle className="w-4 h-4" /> So funktioniert's
          </div>
          <ul className="space-y-1 text-white/70 text-[13px] leading-relaxed">
            <li>• Du wettest, dass etwas <span className="text-green-400 font-medium">eintritt</span>. Einsatz × Quote = Auszahlung.</li>
            <li>• Bsp: <span className="text-white">10 auf x3,0 → 30 Coins</span>. Je unwahrscheinlicher, desto höher die Quote.</li>
            <li>• Max. <span className="text-white font-medium">30%</span> deines Guthabens{balance > 0 ? ` (~${Math.floor(balance * 0.3)})` : ''} pro Wette.</li>
          </ul>
        </div>
      )}

      {/* Predictions List */}
      {visiblePreds.length === 0 ? (
        <div className="text-center py-12 text-white/50">
          <Coins className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Noch keine Predictions verfügbar</p>
        </div>
      ) : (
        <div className="space-y-3">
          {visiblePreds.map((pred) => (
            <PredictionCard
              key={pred.predictionId}
              prediction={pred}
              marketStatus={marketStatus}
              marketPhase={marketPhase}
              balance={balance}
              available={available}
              expanded={expandedPrediction === pred.predictionId}
              onToggle={() => setExpandedPrediction(
                expandedPrediction === pred.predictionId ? null : pred.predictionId
              )}
              onBetPlaced={fetchMarket}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * PredictionCard - Single prediction with betting interface
 */
function PredictionCard({ prediction, marketStatus, marketPhase = 1, balance = 0, available = 0, expanded, onToggle, onBetPlaced }) {
  const betSide = 'YES'; // JA-only Markt: man wettet nur, DASS etwas eintritt
  const [betAmount, setBetAmount] = useState('');
  const [placing, setPlacing] = useState(false);
  const [betError, setBetError] = useState(null);
  const [confirmingResult, setConfirmingResult] = useState(null);
  const [confirmError, setConfirmError] = useState(null);

  const {
    predictionId,
    predictionText,
    type,
    ownerDisplayName,
    teamProbability,
    yesOdds,
    baseYesOdds,
    totalStake = 0,
    betCount = 0,
    bets = [],
    canBet,
    isOwner,
    userPosition,
    authorFreebet,
    settlementResult,
    resolutionStatus,
    authorConfirmation,
    adminConfirmation,
  } = prediction;

  const isSettled = settlementResult !== null;
  // In Phase 2 hat sich die Quote ggf. gegenüber der Startquote verschoben
  const oddsShifted = marketPhase === 2 && baseYesOdds != null && Math.abs((baseYesOdds || 0) - (yesOdds || 0)) >= 0.05;

  // Author confirmation handler
  const handleAuthorConfirm = async (result) => {
    setConfirmingResult(result);
    setConfirmError(null);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Nicht angemeldet");

      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE}/api/predictions-market/${predictionId}/author-confirm`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ result }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `Fehler: ${res.status}`);
      }

      // Success - refresh data
      onBetPlaced?.();
    } catch (err) {
      setConfirmError(err.message);
    } finally {
      setConfirmingResult(null);
    }
  };

  const handlePlaceBet = async () => {
    if (!betSide || !betAmount || placing) return;
    
    const stake = parseFloat(betAmount);
    if (isNaN(stake) || stake <= 0) {
      setBetError("Ungültiger Betrag");
      return;
    }

    setPlacing(true);
    setBetError(null);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Nicht angemeldet");

      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE}/api/predictions-market/place-bet`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          predictionId,
          side: betSide,
          stake,
          requestId: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `Fehler: ${res.status}`);
      }

      // Success
      setBetAmount('');
      onBetPlaced?.();
    } catch (err) {
      setBetError(err.message);
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className={cn(
      "rounded-xl border overflow-hidden",
      isOwner 
        ? "bg-gradient-to-br from-amber-500/10 to-yellow-500/5 border-amber-500/30"
        : "bg-white/5 border-white/10",
      isSettled && "opacity-75"
    )}>
      {/* Header */}
      <div 
        className="p-4 cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            {/* Author & Type Badge */}
            <div className="flex items-center gap-2 mb-2">
              <span className={cn(
                "px-2 py-0.5 rounded text-xs font-medium",
                type === 'on_court' 
                  ? "bg-green-500/20 text-green-400" 
                  : "bg-blue-500/20 text-blue-400"
              )}>
                {type === 'on_court' ? 'ON-COURT' : 'OFF-COURT'}
              </span>
              <span className="text-xs text-white/50">
                von {ownerDisplayName}
              </span>
              {isOwner && (
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-500/20 text-yellow-400">
                  DEINE
                </span>
              )}
            </div>
            
            {/* Prediction Text */}
            <p className="text-white font-medium leading-snug">
              {predictionText}
            </p>

            {/* Team Probability */}
            <div className="mt-2 text-sm text-white/60">
              Team-Tipp: <span className="text-white font-medium">{teamProbability}%</span> Wahrscheinlichkeit
            </div>
          </div>

          {/* Expand Icon */}
          <div className="text-white/40">
            {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
        </div>

        {/* Settlement Badge */}
        {isSettled && (
          <div className={cn(
            "mt-3 flex items-center gap-2 px-3 py-1.5 rounded-lg",
            settlementResult === 'SUCCESS' 
              ? "bg-green-500/20 text-green-400"
              : settlementResult === 'FAIL'
                ? "bg-red-500/20 text-red-400"
                : "bg-gray-500/20 text-gray-400"
          )}>
            {settlementResult === 'SUCCESS' ? (
              <>
                <CheckCircle size={16} />
                <span className="font-medium">EINGETROFFEN</span>
              </>
            ) : settlementResult === 'FAIL' ? (
              <>
                <XCircle size={16} />
                <span className="font-medium">NICHT EINGETROFFEN</span>
              </>
            ) : (
              <>
                <AlertCircle size={16} />
                <span className="font-medium">ABGEBROCHEN</span>
              </>
            )}
          </div>
        )}

        {/* Quick Odds + Pot Display */}
        {!expanded && !isSettled && (
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs text-white/50">Quote wenn's eintritt:</span>
              <OddsBadge value={yesOdds} mode={marketPhase === 2 ? 'LIVE' : 'FIXED'} size="sm" />
              {oddsShifted && (
                <span className="text-[11px] text-white/40">
                  (Start x{baseYesOdds.toFixed(2)} {yesOdds < baseYesOdds ? '↓' : '↑'})
                </span>
              )}
              <span className="text-[11px] text-white/40">10 → <span className="text-green-300 font-medium">{(10 * yesOdds).toFixed(0)}</span></span>
            </div>
            <div className="flex items-center gap-3 text-[11px]">
              <span className="px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-300 font-medium" data-testid={`pot-${predictionId}`}>
                💰 {Math.round(totalStake)} Coins im Topf
              </span>
              <span className="text-white/40">{betCount} {betCount === 1 ? 'Wetter' : 'Wetter'}</span>
            </div>
          </div>
        )}
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-white/10 pt-4 space-y-4">
          {/* Odds Display */}
          <div className="bg-green-500/10 rounded-lg p-4 text-center border border-green-500/20">
            <div className="text-xs text-green-400 mb-1">
              Quote — wenn's eintritt {marketPhase === 2 && <span className="text-purple-300">(live)</span>}
            </div>
            <div className="text-3xl font-bold text-green-300">x{yesOdds.toFixed(2)}</div>
            {oddsShifted ? (
              <div className="text-[11px] text-white/50 mt-1">
                Startquote war <span className="line-through">x{baseYesOdds.toFixed(2)}</span> · {yesOdds < baseYesOdds ? 'gesunken (viel gewettet)' : 'gestiegen (wenig gewettet)'}
              </div>
            ) : (
              <div className="text-[11px] text-white/40 mt-1">10 Coins → {(10 * yesOdds).toFixed(0)} zurück</div>
            )}
          </div>

          {/* Transparenz: Wer hat gewettet */}
          <div className="bg-white/5 rounded-lg p-3 border border-white/10" data-testid={`bettors-${predictionId}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-white/80">💰 Topf: {Math.round(totalStake)} Coins · {betCount} Wetter</span>
            </div>
            {bets.length === 0 ? (
              <p className="text-[12px] text-white/40">Noch niemand hat gewettet — hohe Quote, deine Chance! 🎯</p>
            ) : (
              <ul className="space-y-1">
                {bets.map((b, i) => (
                  <li key={i} className="flex items-center justify-between text-[12px]">
                    <span className={cn("truncate", b.isMe ? "text-yellow-300 font-medium" : "text-white/70")}>
                      {b.isMe ? 'Du' : b.displayName}
                      {b.isFreebet && <span className="ml-1 text-amber-400/70">(Bonus)</span>}
                      {b.phase === 2 && <span className="ml-1 text-purple-400/60">·P2</span>}
                    </span>
                    <span className="text-white/80 tabular-nums">
                      {Math.round(b.stake)} <span className="text-white/40">@ x{(b.odds || 0).toFixed(2)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Author Freebet Display */}
          {isOwner && authorFreebet && (
            <div className="bg-gradient-to-r from-amber-500/20 to-yellow-500/10 rounded-lg p-4 border border-amber-500/30">
              <div className="flex items-center gap-2 mb-2">
                <Gift className="w-5 h-5 text-amber-400" />
                <span className="font-medium text-amber-300">Dein Autoren-Obolus</span>
              </div>
              <div className="text-sm text-white/80">
                <span className="text-amber-400 font-bold">{authorFreebet.stake}</span> Bonus-Coins auf JA @ x{authorFreebet.odds?.toFixed(2)}
              </div>
              <div className="mt-1 text-sm text-green-400">
                Möglicher Bonusgewinn: +{authorFreebet.potentialProfit?.toFixed(0)} Coins
              </div>
              {authorFreebet.status !== 'PENDING' && (
                <div className={cn(
                  "mt-2 text-sm font-medium",
                  authorFreebet.status === 'WON' ? "text-green-400" : "text-white/50"
                )}>
                  Status: {authorFreebet.status}
                </div>
              )}
            </div>
          )}

          {/* Author Confirmation Panel — rollierend: jederzeit möglich, sobald eingetreten */}
          {isOwner && !isSettled && (
            <div className="bg-gradient-to-r from-purple-500/20 to-blue-500/10 rounded-lg p-4 border border-purple-500/30">
              <div className="text-sm font-medium text-purple-300 mb-3">
                🎯 Ergebnis bestätigen
              </div>
              
              {/* Current confirmation status */}
              {(authorConfirmation || adminConfirmation) && (
                <div className="mb-3 text-xs space-y-1">
                  {authorConfirmation && (
                    <div className="flex items-center gap-2">
                      <span className="text-white/50">Deine Bestätigung:</span>
                      <span className={cn(
                        "font-medium",
                        authorConfirmation.result === 'SUCCESS' ? 'text-green-400' :
                        authorConfirmation.result === 'FAIL' ? 'text-red-400' : 'text-gray-400'
                      )}>
                        {authorConfirmation.result === 'SUCCESS' ? '✓ EINGETROFFEN' :
                         authorConfirmation.result === 'FAIL' ? '✗ NICHT EINGETROFFEN' : '○ VOID'}
                      </span>
                    </div>
                  )}
                  {adminConfirmation && (
                    <div className="flex items-center gap-2">
                      <span className="text-white/50">Admin-Bestätigung:</span>
                      <span className={cn(
                        "font-medium",
                        adminConfirmation.result === 'SUCCESS' ? 'text-green-400' :
                        adminConfirmation.result === 'FAIL' ? 'text-red-400' : 'text-gray-400'
                      )}>
                        {adminConfirmation.result === 'SUCCESS' ? '✓ EINGETROFFEN' :
                         adminConfirmation.result === 'FAIL' ? '✗ NICHT EINGETROFFEN' : '○ VOID'}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Resolution status */}
              {resolutionStatus === 'DISPUTED' && (
                <div className="mb-3 bg-yellow-500/20 text-yellow-400 px-3 py-2 rounded text-sm">
                  ⚠️ Widerspruch! Autor und Admin haben unterschiedliche Ergebnisse angegeben.
                </div>
              )}

              {/* Confirmation buttons */}
              {!authorConfirmation && (
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleAuthorConfirm('SUCCESS')}
                    disabled={confirmingResult !== null}
                    className="bg-green-600 hover:bg-green-700 text-xs"
                  >
                    {confirmingResult === 'SUCCESS' ? '...' : '✓ Eingetroffen'}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleAuthorConfirm('FAIL')}
                    disabled={confirmingResult !== null}
                    className="bg-red-600 hover:bg-red-700 text-xs"
                  >
                    {confirmingResult === 'FAIL' ? '...' : '✗ Nicht'}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleAuthorConfirm('VOID')}
                    disabled={confirmingResult !== null}
                    className="bg-gray-600 hover:bg-gray-700 text-xs"
                  >
                    {confirmingResult === 'VOID' ? '...' : '○ Void'}
                  </Button>
                </div>
              )}

              {confirmError && (
                <div className="mt-2 text-red-400 text-xs">
                  {confirmError}
                </div>
              )}

              {authorConfirmation && !adminConfirmation && (
                <div className="text-xs text-white/50">
                  ⏳ Warte auf Admin-Bestätigung...
                </div>
              )}
            </div>
          )}

          {/* User Position */}
          {userPosition && userPosition.totalYesStake > 0 && (
            <div className="bg-white/5 rounded-lg p-3">
              <div className="text-xs text-white/50 mb-2">Deine Wette</div>
              <div className="flex items-center justify-between">
                <span className="text-green-400">Gesetzt</span>
                <CoinDisplay amount={userPosition.totalYesStake} size="sm" />
              </div>
            </div>
          )}

          {/* Betting Interface — JA-only */}
          {canBet && marketStatus === 'OPEN' && !isSettled && (() => {
            const alreadyStaked = (userPosition?.totalYesStake || 0);
            const maxPerPrediction = Math.floor(balance * 0.3);
            // Nicht mehr als 30%/Prediction UND nicht mehr als frei verfügbar
            const remaining = Math.max(0, Math.min(maxPerPrediction - alreadyStaked, Math.floor(available)));
            const odds = yesOdds;
            const stakeNum = parseFloat(betAmount) || 0;
            const chips = [10, 25, 50].filter((c) => c <= remaining);
            return (
            <div className="space-y-3">
              <div className="text-sm text-white/70">
                Du wettest, dass das <span className="text-green-400 font-medium">eintritt</span> · Quote <span className="text-green-300 font-bold">x{yesOdds.toFixed(2)}</span>
                {marketPhase === 2 && <span className="text-purple-300/80 text-xs ml-1">(live — ändert sich mit dem Einsatz)</span>}
              </div>

              {/* Max/30% Hinweis */}
              <div className="text-xs text-white/50 flex items-center justify-between">
                <span>Max. 30% / Wette · {Math.floor(available)} frei</span>
                <span className="text-white/70">noch <span className="text-yellow-300 font-medium">{remaining}</span> Coins setzbar</span>
              </div>

              {/* Amount Input */}
              <div className="space-y-2">
                {/* Quick-Stake Chips */}
                <div className="flex gap-2">
                  {chips.map((c) => (
                    <button
                      key={c}
                      onClick={() => setBetAmount(String(c))}
                      className="px-3 py-1.5 rounded-full text-xs font-medium bg-white/10 hover:bg-white/20 text-white"
                    >
                      {c}
                    </button>
                  ))}
                  {remaining > 0 && (
                    <button
                      onClick={() => setBetAmount(String(remaining))}
                      className="px-3 py-1.5 rounded-full text-xs font-medium bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300"
                    >
                      Max ({remaining})
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Einsatz"
                    value={betAmount}
                    onChange={(e) => setBetAmount(e.target.value)}
                    className="bg-black/30 border-white/20"
                  />
                  <Button
                    onClick={handlePlaceBet}
                    disabled={placing || !betAmount}
                    className="px-6 bg-green-600 hover:bg-green-700"
                  >
                    {placing ? "..." : "Wetten"}
                  </Button>
                </div>

                {stakeNum > 0 && (
                  <div className="rounded-lg bg-black/30 border border-white/10 px-3 py-2 text-sm flex items-center justify-between">
                    <span className="text-white/60">Einsatz {stakeNum} @ x{odds.toFixed(2)}</span>
                    <span className="text-white">
                      → <span className="font-bold text-green-300">{(stakeNum * odds).toFixed(0)}</span> Coins
                      <span className="text-green-400/70 text-xs ml-1">(+{(stakeNum * odds - stakeNum).toFixed(0)})</span>
                    </span>
                  </div>
                )}

                {betError && (
                  <div className="text-sm text-red-400 flex items-center gap-2">
                    <AlertCircle size={14} />
                    {betError}
                  </div>
                )}
              </div>
            </div>
            );
          })()}

          {/* Owner Notice */}
          {isOwner && !isSettled && (
            <div className="text-center text-sm text-amber-400/70 py-2">
              Du kannst nicht auf deine eigene Prediction wetten
            </div>
          )}

          {/* Market Closed Notice */}
          {marketStatus !== 'OPEN' && !isSettled && !isOwner && (
            <div className="text-center text-sm text-yellow-400/70 py-2 flex items-center justify-center gap-2">
              <Lock size={14} />
              Markt ist geschlossen
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PredictionMarket;
